import { escapeHtml } from "@common/helpers/escapeHtml";

/** A rectangle of cell texts, row-major. */
export type TextGrid = ReadonlyArray<readonly string[]>;

const NEEDS_QUOTES = /[\t\n\r"]/;

const quoteField = (field: string) =>
    NEEDS_QUOTES.test(field) ? `"${field.replaceAll('"', '""')}"` : field;

/**
 * Tab-separated text as spreadsheets write it: fields with tabs, newlines, or
 * quotes are quoted, and every row ends in a newline, so a blank last row
 * survives `parseTsv` and a blank cell copies as `"\n"`, not as nothing.
 */
export const toTsv = (grid: TextGrid) =>
    grid.map((row) => `${row.map(quoteField).join("\t")}\n`).join("");

const toHtmlCell = (field: string) => `<td>${escapeHtml(field)}</td>`;

const toHtmlRow = (row: readonly string[]) =>
    `<tr>${row.map(toHtmlCell).join("")}</tr>`;

/** An HTML table, so rich editors (Docs, Notion, email) paste a real table. */
export const toHtml = (grid: TextGrid) =>
    `<table><tbody>${grid.map(toHtmlRow).join("")}</tbody></table>`;

/**
 * Parses tab-separated text with spreadsheet quoting: a field that starts with
 * a quote runs to the closing quote (or the end of the text) and may hold
 * tabs, line breaks, and `""`, kept as written. Outside quotes, LF, CRLF, and
 * CR end a row, and one trailing line break (`toTsv` and Excel add it) does
 * not make an empty last row.
 */
export const parseTsv = (text: string): string[][] => {
    const rows: string[][] = [];
    let row: string[] = [];
    let field = "";
    let index = 0;
    const source = text.replace(/\r?\n$|\r$/, "");
    while (index <= source.length) {
        const char = source[index];
        if (field === "" && char === '"') {
            const end = findClosingQuote(source, index + 1);
            field = source
                .slice(index + 1, end)
                .replaceAll('""', '"')
                .replaceAll("\r\n", "\n");
            index = Math.min(end + 1, source.length);
            continue;
        }
        if (char === "\t") {
            row.push(field);
            field = "";
        } else if (char === "\n" || char === "\r" || char === undefined) {
            row.push(field);
            rows.push(row);
            row = [];
            field = "";
            if (char === "\r" && source[index + 1] === "\n") {
                index += 1;
            }
        } else {
            field += char;
        }
        index += 1;
    }
    return rows;
};

const findClosingQuote = (source: string, from: number) => {
    let index = from;
    while (index < source.length) {
        if (source[index] === '"') {
            if (source[index + 1] !== '"') {
                return index;
            }
            index += 1;
        }
        index += 1;
    }
    return source.length;
};

/**
 * Excel's paste extent: a clip repeats across a target that is a whole
 * multiple of its size on that axis; otherwise it pastes once at its own size.
 */
export const tileClip = ({
    clip,
    rowCount,
    columnCount,
}: {
    clip: TextGrid;
    rowCount: number;
    columnCount: number;
}): string[][] => {
    const clipRows = clip.length;
    const clipColumns = clip.reduce((max, row) => Math.max(max, row.length), 0);
    if (clipRows === 0 || clipColumns === 0) {
        return [];
    }
    const rows = rowCount % clipRows === 0 ? rowCount : clipRows;
    const columns = columnCount % clipColumns === 0 ? columnCount : clipColumns;
    return Array.from({ length: rows }, (_, rowIndex) =>
        Array.from(
            { length: columns },
            (__, columnIndex) =>
                clip[rowIndex % clipRows]?.[columnIndex % clipColumns] ?? "",
        ),
    );
};
